import React, { useEffect, useState } from 'react';
import { createUseStyles } from 'src/theming';
import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  TextField,
} from '@material-ui/core';
import { Controller, useForm } from 'react-hook-form';
import DateTimePicker from '../../../../../../../components/DateTimePicker';
import { AddInterventionOptions } from 'src/interfaces/api/Intervention';
import moment from 'moment';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';

const useStyles = createUseStyles({
  title: {
    width: 400,
    '& h2': {
      color: 'rgba(0, 0, 0, 0.87)',
    },
  },
  error: {
    color: '#f44336',
    fontSize: '.75rem',
    padding: '0 25px',
  },
  formControl: {
    marginBottom: '16px !important',
  },
  checkboxContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '-23px !important',
  },
});

interface Props {
  open: boolean;
  onClose: () => void;
  onValidate: (addInterventionOptions: AddInterventionOptions) => void;
}

const DURATION_STEP = 15;

const defaultFormValues: AddInterventionOptions = {
  description: '',
  onSite: false,
  startDate: moment().format('YYYY-MM-DD HH:mm:ss'),
  endDate: moment().format('YYYY-MM-DD HH:mm:ss'),
};

const AddInterventionDialog: React.FC<Props> = ({ open, onClose, onValidate }) => {
  const classes = useStyles();
  const [duration, setDuration] = useState(0);
  const [formError, setFormError] = useState<string | undefined>();

  const {
    reset,
    control,
    setValue,
    getValues,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<AddInterventionOptions>({
    defaultValues: {
      ...defaultFormValues,
    },
  });

  useEffect(() => {
    setFormError(undefined);
  }, []);

  const onSubmit = async (addInterventionOptions: AddInterventionOptions) => {
    try {
      setFormError(undefined);
      await onValidate(addInterventionOptions);
      onClose();
      reset(defaultFormValues);
    } catch (e) {
      setFormError(e.message);
    }
  };

  const handleChangeDuration = (event: React.ChangeEvent<HTMLInputElement>) => {
    const duration = parseInt(event?.target?.value || '0', 10);
    const startDateValue = getValues('startDate');

    if (startDateValue && duration >= 0) {
      const startDate = moment(startDateValue, 'YYYY-MM-DD HH:mm:ss');
      startDate.add(duration, 'minute');
      setValue('endDate', startDate.format('YYYY-MM-DD HH:mm:ss'));
      setDuration(duration);
    }
  };

  const handleChangeEndDate = (date: MaterialUiPickersDate) => {
    const startDateValue = getValues('startDate');

    if (date && startDateValue) {
      const startDate = moment(startDateValue, 'YYYY-MM-DD HH:mm:ss');
      const duration = date.diff(startDate, 'minute');

      if (duration < 0) {
        setValue('startDate', date.format('YYYY-MM-DD HH:mm:ss'));
        setDuration(0);
      } else {
        setDuration(duration);
      }
    }
  };

  const handleChangeStartDate = (date: MaterialUiPickersDate) => {
    if (date) {
      const endDate = moment(date).add(duration, 'minute') || moment();
      setValue('endDate', endDate.format('YYYY-MM-DD HH:mm:ss'));
    }
  };

  useEffect(() => {
    setFormError(undefined);
  }, []);

  return (
    <Dialog onClose={onClose} scroll='paper' open={open}>
      <DialogTitle className={classes.title}>Ajouter une intervention</DialogTitle>

      <DialogContent>
        <MuiPickersUtilsProvider utils={MomentUtils}>
          <Grid container justifyContent='space-between' spacing={1}>
            <Grid item xs={4}>
              <Controller
                name='startDate'
                control={control}
                rules={{ required: { value: true, message: 'Le champ est requis' } }}
                render={({ field: { onChange, ...fieldOthers }, fieldState: { error } }) => (
                  <DateTimePicker
                    {...fieldOthers}
                    onChange={(date) => {
                      handleChangeStartDate(date);
                      onChange(date ? date.format('YYYY-MM-DD HH:mm:ss') : undefined);
                    }}
                    className={classes.formControl}
                    label="Début de l'intervention"
                    format='DD/MM/YYYY HH:mm'
                    inputVariant='outlined'
                    helperText={error?.message}
                    error={undefined !== error}
                    fullWidth
                    ampm={false}
                  />
                )}
              />
            </Grid>

            <Grid item xs={3}>
              <TextField
                className={classes.formControl}
                label='Durée (min.)'
                type='number'
                InputProps={{ inputProps: { min: 0, step: DURATION_STEP } }}
                onChange={handleChangeDuration}
                value={duration > 0 ? duration : ''}
                variant='outlined'
                fullWidth
              />
            </Grid>

            <Grid item xs={4}>
              <Controller
                name='endDate'
                control={control}
                rules={{ required: { value: true, message: 'Le champ est requis' } }}
                render={({ field: { onChange, ...fieldOthers }, fieldState: { error } }) => (
                  <DateTimePicker
                    {...fieldOthers}
                    onChange={(date) => {
                      handleChangeEndDate(date);
                      onChange(date ? date.format('YYYY-MM-DD HH:mm:ss') : undefined);
                    }}
                    className={classes.formControl}
                    label="Fin de l'intervention"
                    format='DD/MM/YYYY HH:mm'
                    inputVariant='outlined'
                    helperText={error?.message}
                    error={undefined !== error}
                    disablePast
                    fullWidth
                    ampm={false}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name='description'
                control={control}
                rules={{ required: { value: true, message: 'Le champ est requis' } }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    className={classes.formControl}
                    label='Description'
                    placeholder="Description de l'intervention"
                    variant='outlined'
                    multiline
                    rows={3}
                    fullWidth
                    error={undefined !== error}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid>

            <Grid className={classes.checkboxContainer} item xs={12}>
              <Controller
                name='onSite'
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Checkbox />}
                    label='Intervention sur site'
                    labelPlacement='start'
                    {...field}
                  />
                )}
              />
            </Grid>
          </Grid>
        </MuiPickersUtilsProvider>
      </DialogContent>

      <DialogActions>
        <Button type='button' onClick={onClose} color='primary'>
          Annuler
        </Button>

        <Button type='submit' color='primary' disabled={isSubmitting} onClick={handleSubmit(onSubmit)}>
          {isSubmitting && <CircularProgress color='primary' size={15} />} Ajouter
        </Button>
      </DialogActions>

      {formError && <p className={classes.error}>{formError}</p>}
    </Dialog>
  );
};

export default AddInterventionDialog;
